import type { ToolBundle } from '../../types/prioritization-selector';

export const toolBundles: ToolBundle[] = [
  {
    id: 'B1',
    name: 'Solo-Now',
    description: 'Today/this week, low ceremony',
    useCase: 'Personal immediate decisions for today or this week',
    tools: ['Capture inbox', 'Quick 2×2 (Impact/Effort)', 'Stack rank', 'Calendar time-block', '2-minute rule'],
    process: [
      'Capture inbox → quick 2×2 (Impact/Effort) → stack rank → calendar time-block',
      'Add "2-minute rule" + next-best alternative if blocked'
    ],
    output: 'Top 3 + protected time blocks + "don\'t do" list',
    complexity: 'low',
    timeRequired: 'minutes',
    teamSize: 'individual',
    scenario: 'Personal immediate decisions',
    plainLanguageQuestion: 'Are you deciding just for yourself and only for the next day or two?'
  },
  {
    id: 'B2',
    name: 'Solo-Week',
    description: 'Light planning ritual',
    useCase: 'Personal weekly planning with light structure',
    tools: ['Weekly review checklist', 'Grouped buckets', 'Impact/effort', 'Capacity fit to calendar'],
    process: [
      'Weekly review checklist → grouped buckets → impact/effort → capacity fit to calendar'
    ],
    output: 'Weekly plan, 3 priorities, 5 "nice-to-haves," parking lot',
    complexity: 'low',
    timeRequired: 'hours',
    teamSize: 'individual',
    scenario: 'Personal weekly planning',
    plainLanguageQuestion: 'Are you deciding just for yourself and planning out your week?'
  },
  {
    id: 'B3',
    name: 'Team-Backlog',
    description: 'Delivery focus',
    useCase: 'Team task prioritization for delivery',
    tools: ['Definition-of-Ready check', 'De-dupe/merge', 'MoSCoW or RICE', 'Kanban limits', 'Capacity check'],
    process: [
      'Definition-of-Ready check → de-dupe/merge → MoSCoW or RICE → Kanban limits → capacity check'
    ],
    output: 'Ordered backlog, WIP limits, next sprint/iteration plan',
    complexity: 'medium',
    timeRequired: 'hours',
    teamSize: 'small',
    scenario: 'Team delivery prioritization',
    plainLanguageQuestion: 'Are you organizing a shared list of work for a team to deliver over time?'
  },
  {
    id: 'B4',
    name: 'Team-Decision',
    description: 'Consent/consensus facilitation',
    useCase: 'Team decision-making requiring consensus',
    tools: ['Framing brief', 'Dot/Roman voting', 'Consent check', 'Decision log'],
    process: [
      'Framing brief (problem, criteria, constraints) → dot/Roman voting → consent check → decision log'
    ],
    output: 'Decision + rationale + owner + review date',
    complexity: 'high',
    timeRequired: 'hours',
    teamSize: 'small',
    scenario: 'Team consensus decisions',
    plainLanguageQuestion: 'Does a group need to agree on a single choice together right now?'
  },
  {
    id: 'B5',
    name: 'Portfolio-Quarter',
    description: 'Multi-team/initiative',
    useCase: 'Portfolio-level planning across multiple teams',
    tools: ['Strategy/OKR alignment', 'WSJF/Cost of Delay', 'Capacity + dependency map', 'Quarterly roadmap'],
    process: [
      'Strategy/OKR alignment → WSJF/Cost of Delay → capacity + dependency map → quarterly roadmap'
    ],
    output: 'Ranked portfolio, cut line, sequencing, comms deck',
    complexity: 'high',
    timeRequired: 'days',
    teamSize: 'large',
    scenario: 'Portfolio and multi-team planning',
    plainLanguageQuestion: 'Are you ranking multiple big projects across teams for the next quarter or longer?'
  },
  {
    id: 'B6',
    name: 'Discovery-Unknowns',
    description: 'High uncertainty',
    useCase: 'Experimental work with high uncertainty',
    tools: ['Opportunity-solution tree', 'Assumption mapping', 'Test backlog (ICE)', 'Learning review cadence'],
    process: [
      'Opportunity-solution tree → assumption mapping → test backlog (ICE) → learning review cadence'
    ],
    output: 'Prioritized experiments, kill/continue rules, roll-in to B3/B5 when validated',
    complexity: 'high',
    timeRequired: 'days',
    teamSize: 'small',
    scenario: 'High uncertainty and discovery work',
    plainLanguageQuestion: 'Are you prioritizing work where the problem, solution, or outcome is still uncertain?'
  },
  {
    id: 'B7',
    name: 'Dependency-Heavy',
    description: 'Integration/program',
    useCase: 'Complex work with many dependencies',
    tools: ['Program board (cross-team)', 'Dependency matrix', 'Critical path check', 'Risk & buffer rules'],
    process: [
      'Program board (cross-team) → dependency matrix → critical path check → risk & buffer rules'
    ],
    output: 'Sequencing with interlocks, sync cadence, change protocol',
    complexity: 'high',
    timeRequired: 'days',
    teamSize: 'large',
    scenario: 'Complex dependencies and integration',
    plainLanguageQuestion: 'Does the work depend on other teams or has many moving parts that must line up?'
  },
  {
    id: 'B8',
    name: 'High-Stakes Safeguards',
    description: 'Irreversible or costly to reverse',
    useCase: 'Critical decisions that are hard to reverse',
    tools: ['Pre-mortem', 'Decision memo', 'Risk register', 'Escalation roles', 'Guardrails & kill criteria'],
    process: [
      'Pre-mortem → decision memo → risk register → escalation roles → guardrails & kill criteria'
    ],
    output: 'Approved decision packet + safeguards + review triggers',
    complexity: 'high',
    timeRequired: 'days',
    teamSize: 'medium',
    scenario: 'High-stakes irreversible decisions',
    plainLanguageQuestion: 'Is this a one-way decision that\'s expensive or impossible to undo?'
  },
  {
    id: 'B9',
    name: 'Delegation-For-Others',
    description: 'You prioritize, others execute',
    useCase: 'Prioritizing work for others to execute',
    tools: ['IOGC brief (Intent, Outcome, Guardrails, Check-ins)', 'RACI', 'Decision log'],
    process: [
      'Intent (why), Outcome (what), Guardrails (how far), Check-ins (when) → RACI → decision log'
    ],
    output: 'Clear handoff note + evidence of alignment',
    complexity: 'medium',
    timeRequired: 'hours',
    teamSize: 'medium',
    scenario: 'Delegating prioritization to others',
    plainLanguageQuestion: 'Are you setting priorities for someone else to carry out?'
  },
  {
    id: 'B10',
    name: 'Cross-Team Alignment',
    description: 'Visibility & buy-in',
    useCase: 'Cross-team coordination and alignment',
    tools: ['Single-slide brief', 'Alignment review', 'Feedback window', 'Commit & broadcast'],
    process: [
      'Single-slide brief → alignment review → feedback window → commit & broadcast'
    ],
    output: 'Final list + rationale + visual (heatmap or 2×2)',
    complexity: 'medium',
    timeRequired: 'hours',
    teamSize: 'large',
    scenario: 'Cross-team alignment and coordination',
    plainLanguageQuestion: 'Do you need cross-team alignment and buy-in?'
  }
];

export const getBundleById = (id: string): ToolBundle | undefined => {
  return toolBundles.find(bundle => bundle.id === id);
};

export const getBundlesByIds = (ids: string[]): ToolBundle[] => {
  return ids.map(id => getBundleById(id)).filter(Boolean) as ToolBundle[];
};

